import _ from "lodash";
import {
  DEMAND_TYPES,
  PRESETS,
  UNFILLED,
  getGoogleEligibleDemandTypes,
  getPrebidDemandTypes,
  getGuarenteedDemandTypes,
} from "constants/DemandType";
import { VIEW } from "components/demand-portfolio/DataHelper";
import numeral from "numeral";

// ex. 1234567 => $1,234,567.00
export function formatMoneyWithCurrency({ currency, value }) {
  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency,
  });
}

// ex. 1234567 => $1.23m
export function formatMoneyWithCurrencySuffix({ currency, value }) {
  const formattedNumber = formatNumSuffix(value);
  const currencySymbol = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
    .formatToParts(0)
    .find((part) => part.type === "currency").value;

  return `${currencySymbol}${formattedNumber}`;
}

export const HOLISTIC_DEMAND_TYPES = [
  DEMAND_TYPES.AD_EXCHANGE,
  DEMAND_TYPES.AD_SENSE,
  DEMAND_TYPES.PRICE_PRIORITY,
  DEMAND_TYPES.EXCHANGE_BIDDING, // open bidding included
  DEMAND_TYPES.NETWORK,
  DEMAND_TYPES.BULK,
  DEMAND_TYPES.HEADER_BIDDING,
  DEMAND_TYPES.BILLABLE_HOUSE,
  DEMAND_TYPES.SDK_BIDDING,
];

const UNFILLED_COLOR = "#B1B1B1";

export const REV_LIFT_COLOR = "#FF7A00";
export const REV_LIFT_COLOR_CVD = "#A05A00";

// #1797FF,#4C44C7,#008C54,#FFC41D,#C3FBFF,#B146F0,#1CE691,#9FA4FF,#FF8FB0,#1D4E76,#D2B17F,#575957,#C9F591,#E6452F
export const DEMAND_TYPE_COLORS = new Map([
  [DEMAND_TYPES.AD_EXCHANGE, "#1797FF"],
  [DEMAND_TYPES.AD_SENSE, "#4C44C7"],
  [DEMAND_TYPES.EXCHANGE_BIDDING, "#008C54"],
  [DEMAND_TYPES.PRICE_PRIORITY, "#FFC41D"],
  [DEMAND_TYPES.NETWORK, "#C3FBFF"],
  [DEMAND_TYPES.BULK, "#B146F0"],
  [DEMAND_TYPES.HEADER_BIDDING, "#1CE691"],
  [DEMAND_TYPES.BILLABLE_HOUSE, "#9FA4FF"],
  [DEMAND_TYPES.MEDIATION, "#FF8FB0"],
  [DEMAND_TYPES.SDK_BIDDING, "#1D4E76"],
  [DEMAND_TYPES.SPONSORSHIP, "#D2B17F"],
  [DEMAND_TYPES.STANDARD, "#575957"],
  [DEMAND_TYPES.PREFERRED_DEAL, "#C9F591"],
  [DEMAND_TYPES.HOUSE, "#E6452F"],
  [UNFILLED, UNFILLED_COLOR],
]);

// #0072B2,#D25D04,#009E73,#F0E442,#CC79A7,#56B4E9,#E69F00,#CC6677,#332288,#6699CC,#999933,#88CCAA,#AA4499,#DDDDDD
export const DEMAND_TYPE_COLORS_CVD = new Map([
  [DEMAND_TYPES.AD_EXCHANGE, "#0072B2"],
  [DEMAND_TYPES.AD_SENSE, "#D25D04"],
  [DEMAND_TYPES.EXCHANGE_BIDDING, "#009E73"],
  [DEMAND_TYPES.PRICE_PRIORITY, "#F0E442"],
  [DEMAND_TYPES.NETWORK, "#CC79A7"],
  [DEMAND_TYPES.BULK, "#56B4E9"],
  [DEMAND_TYPES.HEADER_BIDDING, "#E69F00"],
  [DEMAND_TYPES.BILLABLE_HOUSE, "#CC6677"],
  [DEMAND_TYPES.MEDIATION, "#332288"],
  [DEMAND_TYPES.SDK_BIDDING, "#6699CC"],
  [DEMAND_TYPES.SPONSORSHIP, "#999933"],
  [DEMAND_TYPES.STANDARD, "#88CCAA"],
  [DEMAND_TYPES.PREFERRED_DEAL, "#AA4499"],
  [DEMAND_TYPES.HOUSE, "#DDDDDD"],
  [UNFILLED, UNFILLED_COLOR],
]);

export const DEMAND_GROUPS_COLORS = new Map([
  [VIEW.BILLABLE, "#1797FF"],
  [VIEW.GUARANTEED, "#D2B17F"],
  [VIEW.NON_BILLABLE, "#1CE691"],
  [UNFILLED, UNFILLED_COLOR],
]);

export const DEMAND_GROUPS_COLORS_CVD = new Map([
  [VIEW.BILLABLE, "#0072B2"],
  [VIEW.GUARANTEED, "#999933"],
  [VIEW.NON_BILLABLE, "#E69F00"],
  [UNFILLED, UNFILLED_COLOR],
]);

export function _parseData({ columns, records }, field, timeKey, monthList) {
  const fieldIndex = columns.indexOf(field);
  const timeIndex = columns.indexOf(timeKey);

  const result = _.map(
    _.groupBy(records, (record) => record[fieldIndex]),
    (records, name) => {
      const data = {};
      _.forEach(columns, (col, index) => {
        if (col !== field && col !== timeKey) {
          const temp = Array(monthList.length).fill(0);
          records.forEach((record) => {
            const monthIndex = monthList.indexOf(record[timeIndex]);
            temp[monthIndex] = record[index];
          });
          data[col] = temp;
        }
      });

      return {
        [field]: name,
        ...data,
      };
    }
  );
  return result;
}

export function _getSortedOption(
  data,
  valKey,
  uniqKey,
  allOption,
  parseNameFunc = null
) {
  const options = _.map(allOption, (d) => _.toLower(d));
  let total = _.sumBy(data, (item) => _.sum(item[valKey]));

  const sortData = _.orderBy(
    _.map(data, (item) => {
      const sum = _.sum(item[valKey]);
      const percentage = _.round((sum / total) * 100, 1);

      return {
        ...item,
        sum,
        percentage,
      };
    }),
    ["sum"],
    ["desc"]
  );

  const sortDataList = _.map(sortData, (r) => _.toLower(r[uniqKey]));

  const leftList = _.difference(options, sortDataList);

  const result = _.map([...sortDataList, ...leftList], (r) => {
    const item = _.find(sortData, (d) => _.toLower(d[uniqKey]) === r);
    return options.includes(r)
      ? {
          value: r,
          label: `${parseNameFunc ? parseNameFunc(r) : _.startCase(r)} (${
            item ? item.percentage : "0"
          }%)`,
        }
      : undefined;
  }).filter((r) => r);

  return result;
}

export function formatNumber(float) {
  return _.toNumber(float.toFixed(2));
}

// ex. 1234567 => 1,234,567 or 1.23m
export function formatThousands(num, suffix = false) {
  return suffix ? formatNumSuffix(num) : numeral(num).format("0,0.00");
}

// ex. 1234567 => 1.23m
export function formatNumSuffix(number) {
  return numeral(number).format("0.00a");
}

export const DEMAND_TYPE_GROUP = new Map()
  .set("Holistic", HOLISTIC_DEMAND_TYPES)
  .set(PRESETS.GOOGLE_ELIGIBLE, getGoogleEligibleDemandTypes())
  .set(PRESETS.PREBID, getPrebidDemandTypes())
  .set(PRESETS.BILLABLE_HOUSE_ONLY, [DEMAND_TYPES.BILLABLE_HOUSE])
  .set(
    PRESETS.PREBID_WITHOUT_BILLABLE_HOUSE,
    _.filter(getPrebidDemandTypes(), (t) => t !== DEMAND_TYPES.BILLABLE_HOUSE)
  )
  .set(PRESETS.GUARENTEED, getGuarenteedDemandTypes());

export const REGION_GROUP = new Map()
  .set("Asia", [
    "2156",
    "2158",
    "2344",
    "2392",
    "2410",
    "2446",
    "2496",
    "2104",
    "2764",
    "2704",
    "2418",
    "2116",
    "2458",
    "2702",
    "2360",
    "2096",
    "2626",
    "2004",
    "2356",
    "2586",
    "2050",
    "2064",
    "2524",
    "2462",
    "2144",
    "2398",
    "2417",
    "2762",
    "2795",
    "2860",
    "2048",
    "2368",
    "2376",
    "2400",
    "2414",
    "2422",
    "2512",
    "2275",
    "2634",
    "2682",
    "2736",
    "2792",
    "2784",
    "2887",
  ])
  .set("North America", [
    "2840",
    "2124",
    "2484",
    "2060",
    "2044",
    "2052",
    "2388",
    "2780",
    "2659",
    "2662",
    "2670",
    "2214",
    "2332",
  ])
  .set("South America", [
    "2032",
    "2076",
    "2152",
    "2170",
    "2218",
    "2328",
    "2600",
    "2604",
    "2858",
    "2862",
    "2068",
  ])
  .set("Europe", [
    "2250",
    "2276",
    "2826",
    "2040",
    "2756",
    "2528",
    "2442",
    "2492",
    "2208",
    "2246",
    "2352",
    "2578",
    "2752",
    "2234",
    "2744",
    "2380",
    "2724",
    "2620",
    "2300",
    "2336",
    "2674",
    "2470",
    "2020",
    "2643",
    "2112",
    "2804",
    "2203",
    "2530",
    "2703",
    "2642",
    "2100",
    "2498",
    "2008",
    "2070",
    "2191",
    "2499",
    "2807",
    "2688",
    "2900",
  ])
  .set("Africa", [
    "2012",
    "2818",
    "2434",
    "2504",
    "2732",
    "2788",
    "2736",
    "2024",
    "2204",
    "2072",
    "2854",
    "2108",
    "2132",
    "2120",
    "2140",
    "2148",
    "2174",
    "2178",
    "2180",
    "2262",
    "2266",
    "2270",
    "2288",
    "2324",
    "2624",
    "2384",
    "2404",
    "2426",
    "2430",
    "2450",
    "2454",
    "2466",
    "2478",
    "2480",
    "2508",
    "2562",
    "2566",
    "2646",
    "2686",
    "2694",
    "2706",
    "2710",
    "2834",
    "2768",
    "2800",
    "2804",
    "2894",
    "2716",
  ])
  .set("Oceania", [
    "2036",
    "2554",
    "2242",
    "2598",
    "2090",
    "2548",
    "2882",
    "2776",
    "2798",
    "2296",
    "2585",
    "2583",
    "2584",
  ]);
